import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SeoMetaProps {
  title: string;
  description: string;
  keywords?: string;
  pathname: string;
}

const baseUrl = 'https://www.chinese-name.net'; // 更新为实际的网站域名
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Chinese Names - AI Name Generator",
  "url": baseUrl,
  "logo": `${baseUrl}/favicon.png`,
  "description": "Free AI-powered Chinese name generator that creates authentic names with cultural significance",
  "sameAs": [
    "https://www.chinese-name.net",
    "https://ai-chinese-name04.vercel.app"
  ]
};

export function SeoMeta({ title, description, pathname }: SeoMetaProps) {
  const canonicalUrl = `${baseUrl}${pathname}`;
  
  // 为不同页面生成特定的Schema.org标记
  const getPageSpecificSchema = () => {
    const baseSchema = {
      "@context": "https://schema.org",
      "url": canonicalUrl,
      "name": title,
      "description": description
    };

    switch (pathname) {
      case '/':
        return {
          ...baseSchema,
          "@type": "WebApplication",
          "applicationCategory": "UtilityApplication",
          "operatingSystem": "All",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "featureList": [
            "AI-powered Chinese name generation",
            "Cultural significance explanations",
            "Pinyin pronunciation guides",
            "Beautiful name cards",
            "Male and female name options"
          ]
        };
      case '/chinese-name-generator':
        return {
          ...baseSchema,
          "@type": "SoftwareApplication",
          "applicationCategory": "UtilityApplication",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          }
        };
      case '/chinese-boy-names':
        return {
          ...baseSchema,
          "@type": "Article",
          "headline": "200+ Chinese Boy Names and Meanings",
          "articleSection": "Chinese Names",
          "articleBody": "Comprehensive guide to Chinese boy names with meanings, pronunciations, and cultural insights",
          "wordCount": "2500+",
          "author": {
            "@type": "Organization",
            "name": "Chinese Name Net"
          },
          "datePublished": "2025-01-15",
          "dateModified": "2025-01-15"
        };
      case '/chinese-girl-names':
        return {
          ...baseSchema,
          "@type": "Article",
          "headline": "200+ Chinese Girl Names and Meanings",
          "articleSection": "Chinese Names",
          "articleBody": "Comprehensive guide to Chinese girl names with meanings, pronunciations, and cultural insights",
          "wordCount": "2500+",
          "author": {
            "@type": "Organization",
            "name": "Chinese Name Net"
          },
          "datePublished": "2025-01-15",
          "dateModified": "2026-04-18"
        };
      case '/cute-chinese-girl-names':
        return {
          ...baseSchema,
          "@type": "Article",
          "headline": "Cute Chinese Girl Names: Sweet Ideas with Meanings",
          "articleSection": "Chinese Names",
          "articleBody": "Detailed guide to cute Chinese girl names with meanings, pronunciation, style differences, and naming tips.",
          "wordCount": "2200+",
          "author": {
            "@type": "Organization",
            "name": "Chinese Name Net"
          },
          "datePublished": "2026-04-18",
          "dateModified": "2026-04-18"
        };
      case '/chinese-pet-names':
        return {
          ...baseSchema,
          "@type": "Article",
          "headline": "Chinese Pet Names: Dog and Cat Name Ideas",
          "articleSection": "Chinese Names",
          "articleBody": "Detailed guide to Chinese pet names for dogs and cats, including meanings, pronunciation, and naming tips.",
          "wordCount": "2100+",
          "author": {
            "@type": "Organization",
            "name": "Chinese Name Net"
          },
          "datePublished": "2026-04-18",
          "dateModified": "2026-04-18"
        };
      case '/chinese-last-names':
      case '/chinese-surnames':
        return {
          ...baseSchema,
          "@type": "Article",
          "headline": "100 Most Common Chinese Last Names: Origins & Meanings",
          "articleSection": "Chinese Culture",
          "articleBody": "Complete guide to Chinese surnames with meanings, origins, and pronunciation",
          "wordCount": "2000+",
          "author": {
            "@type": "Organization",
            "name": "Chinese Name Net"
          },
          "datePublished": "2025-01-15",
          "dateModified": "2026-04-18"
        };
      case '/funny-chinese-names':
        return {
          ...baseSchema,
          "@type": "Article",
          "headline": "Funny Chinese Names: Playful Ideas and Cultural Tips",
          "articleSection": "Chinese Names",
          "articleBody": "A practical guide to funny Chinese names, silly-sounding name ideas, humor pitfalls, and playful naming strategies.",
          "wordCount": "2300+",
          "author": {
            "@type": "Organization",
            "name": "Chinese Name Net"
          },
          "datePublished": "2026-04-18",
          "dateModified": "2026-04-18"
        };
      case '/chinese-dragon-names':
        return {
          ...baseSchema,
          "@type": "Article",
          "headline": "Chinese Dragon Names: Mythic and Powerful Ideas",
          "articleSection": "Chinese Names",
          "articleBody": "Guide to Chinese dragon names with cultural symbolism, meanings, pronunciation, and fantasy naming directions.",
          "wordCount": "2100+",
          "author": {
            "@type": "Organization",
            "name": "Chinese Name Net"
          },
          "datePublished": "2026-04-18",
          "dateModified": "2026-04-18"
        };
      case '/cultural-insights':
        return {
          ...baseSchema,
          "@type": "Article",
          "articleSection": "Education",
          "articleBody": "Information about Chinese naming culture and traditions",
          "educationalLevel": "General",
          "wordCount": "1500+"
        };
      case '/faq':
        return {
          ...baseSchema,
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How to choose a Chinese name?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Choose a Chinese name by considering meaning, pronunciation, cultural appropriateness, and personal connection. Our AI generator automatically handles these factors."
              }
            },
            {
              "@type": "Question", 
              "name": "Can foreigners have Chinese names?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, foreigners can adopt Chinese names for cultural, business, or personal reasons. It's common and widely accepted."
              }
            },
            {
              "@type": "Question",
              "name": "How to pronounce Chinese names correctly?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Chinese names use tones indicated by pinyin. Practice with tone marks and native speakers for accurate pronunciation."
              }
            }
          ]
        };
      case '/blog':
        return {
          ...baseSchema,
          "@type": "Blog",
          "blogPost": [
            {
              "@type": "BlogPosting",
              "headline": "How to Choose a Chinese Name: Complete Beginner's Guide 2025",
              "url": `${baseUrl}/blog/how-to-choose-chinese-name`,
              "datePublished": "2025-06-12",
              "keywords": "how to choose a chinese name, chinese name for beginners"
            },
            {
              "@type": "BlogPosting", 
              "headline": "Chinese Names and Meanings: Understanding the Cultural Significance",
              "url": `${baseUrl}/blog/chinese-names-meanings`,
              "datePublished": "2025-06-12",
              "keywords": "chinese names and meanings, chinese name meaning dictionary"
            },
            {
              "@type": "BlogPosting",
              "headline": "How to Pronounce Chinese Names: Complete Guide with Audio Examples", 
              "url": `${baseUrl}/blog/chinese-names-pronunciation`,
              "datePublished": "2025-06-12",
              "keywords": "how to pronounce chinese names, chinese name pronunciation guide"
            }
          ]
        };
      case '/blog/how-to-choose-chinese-name':
      case '/blog/chinese-names-meanings':
      case '/blog/chinese-names-pronunciation':
        return {
          ...baseSchema,
          "@type": "BlogPosting",
          "headline": title,
          "datePublished": "2025-06-12",
          "dateModified": "2025-06-12",
          "author": {
            "@type": "Organization",
            "name": "Chinese Names Generator"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Chinese Names Generator",
            "logo": {
              "@type": "ImageObject",
              "url": `${baseUrl}/favicon.png`
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": canonicalUrl
          },
          "articleSection": "Chinese Culture",
          "wordCount": "2000+",
          "image": `${baseUrl}/favicon.png`
        };
      default:
        return {
          ...baseSchema,
          "@type": "WebPage"
        };
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Favicon */}
      <link rel="apple-touch-icon" sizes="180x180" href="/favicon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta name="theme-color" content="#6366f1" />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={`${baseUrl}/favicon.png`} />
      <meta property="og:site_name" content="Chinese Names - AI Name Generator" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseUrl}/favicon.png`} />
      
      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="language" content="English" />
      <meta name="author" content="Chinese Names Generator" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="generator" content="React, Vite, TypeScript" />
      
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(getPageSpecificSchema())}
      </script>
      {/* BreadcrumbList Schema for functional pages */}
      {(pathname === '/chinese-boy-names' || pathname === '/chinese-girl-names' || pathname === '/chinese-last-names' || pathname === '/cute-chinese-girl-names' || pathname === '/funny-chinese-names' || pathname === '/chinese-pet-names' || pathname === '/chinese-dragon-names') && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": `${baseUrl}/`
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": pathname === '/chinese-boy-names'
                  ? 'Chinese Boy Names'
                  : pathname === '/chinese-girl-names'
                    ? 'Chinese Girl Names'
                    : pathname === '/cute-chinese-girl-names'
                      ? 'Cute Chinese Girl Names'
                      : pathname === '/funny-chinese-names'
                        ? 'Funny Chinese Names'
                        : pathname === '/chinese-pet-names'
                          ? 'Chinese Pet Names'
                          : pathname === '/chinese-dragon-names'
                            ? 'Chinese Dragon Names'
                        : 'Chinese Last Names',
                "item": `${baseUrl}${pathname}`
              }
            ]
          })}
        </script>
      )}
    </Helmet>
  );
}
